import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const Leoyearly = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for LEO
  const staticMeta = {
    title: "AstroBhagya – Free Leo Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Leo yearly horoscope predictions 2026? Discover free Leo astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Leo yearly horoscope predictions 2026, Leo yearly Astrology predictions, Leo annual predictions, Yearly horoscope by Leo zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchLeoYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `leo_yearly_${dateStr}`; 
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Find LEO data (id: 34 or sunShine contains "LEO") 
        const leoData = data.find(
          item => item.id === 34 || item.sunShine?.toUpperCase().includes("LEO")
        );

        if (leoData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, leoData);
          localStorage.setItem(cacheKey, JSON.stringify(leoData));
          setHoroscopeData(leoData);
        } else {
          throw new Error("Leo data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Leo yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Leo Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchLeoYearly(retries - 1);
      } else {
        // FALLBACK DATA - LEO 2026
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Leo 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>2026 asks Leo to slow down and act with wisdom rather than speed. Success comes from thoughtful planning and emotional maturity, not loud actions.</p>
            <h2>Love and Relationships</h2>
            <p>Committed relationships focus on emotional maturity and stability. Building trust through shared effort and honest communication.</p>
            <h2>Career and Finances</h2>
            <p>Professional consistency and responsibility earn respect. Careful budgeting essential for family/health expenses.</p>
            <h2>Health & Wellness</h2>
            <p>Balanced routine prevents fatigue. Meditation and stress management maintain mental strength.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Ruby Stone:</strong> Strengthens confidence and leadership qualities.</li>
              <li><strong>Sun Practice:</strong> Offer water to rising Sun daily for clarity.</li>
              <li><strong>Sunday Charity:</strong> Donate wheat/jaggery for stability.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeoYearly();
  }, [fetchLeoYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Leo 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchLeoYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Leo 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Leooo.webp")}
                alt="Leo"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/cancer" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Cancer Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/virgo" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Virgo Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS PREVIOUS */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Leo Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Leo?",
                  answer: "Leo individuals are known for their confidence, leadership, creativity, and generosity. Ruled by the Sun, they naturally shine and inspire others.",
                },
                {
                  question: "2. What can Leo expect in love and relationships in 2026?",
                  answer: "2026 brings emotional maturity in relationships. Building trust through shared effort and honest communication strengthens bonds.",
                },
                {
                  question: "3. What does 2026 look like for Leo career growth?",
                  answer: "Professional consistency earns respect. Leadership roles require patience and discipline for long-term recognition.",
                },
                {
                  question: "4. What health advice for Leo in 2026?",
                  answer: "Balanced routine prevents burnout. Regular exercise, healthy meals, and meditation maintain energy levels.",
                },
                {
                  question: "5. Key astrological remedies for Leo?",
                  answer: "Ruby stone, morning Sun offerings, and Sunday charity strengthen confidence and stability.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Leoyearly; 
